import api from "@/config/api";
import useGlobalSpinner from "@/context/spinner";
import { Forms } from "@/types/form";
import { Report } from "@/types/report";
import { Params } from "@/types/utils";
import { i18n } from "@/utils/i18n";
import { triggerFileDownload } from "@/utils/helpers";
import { log } from "@/utils/logger";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

const useFooterActions = (currentReviewNotes: string) => {
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<Report["reviewStatus"] | null>(null);
    const [previewDownloading, setPreviewDownloading] = useState(false);
    const setGlobalLoading = useGlobalSpinner(state => state.setLoading);

    const { getValues } = useFormContext<Forms["Report"]>();

    const { oid, sid, rid } = useParams<Params>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const approve = async (reviewStatus: Report["reviewStatus"], nextReviewNotes: string) => {
        try {
            setGlobalLoading(true);

            const values = getValues();

            const report = await api.patch<Report>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}`,
                { data: { ...values, reviewStatus, reviewNotes: nextReviewNotes } },
            );

            queryClient.setQueryData<Report>(["report", rid], {
                ...report,
                reviewNotes: nextReviewNotes,
            });

            queryClient.invalidateQueries({ queryKey: ["report", rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });

            log.success(
                i18n.t("reviewStatusUpdate", {
                    scope: "messages.success",
                    status: i18n.t(`reportStatus.${reviewStatus}`),
                }),
            );
            navigate(`/organizations/${oid}/reports`);
            return true;
        } catch (e) {
            log.error(e);
            return false;
        } finally {
            setGlobalLoading(false);
        }
    };

    const openNoteModal = (reviewStatus: Report["reviewStatus"]) => {
        setSelectedStatus(reviewStatus);
        setNoteDialogOpen(true);
    };

    const closeNoteModal = () => {
        setNoteDialogOpen(false);
        setSelectedStatus(null);
    };

    const submitReview = async () => {
        if (selectedStatus === null) return;
        const success = await approve(selectedStatus, currentReviewNotes ?? "");
        if (success) {
            closeNoteModal();
        }
    };

    const downloadPreview = async (pages: string[], reviewOutcome: string | null) => {
        try {
            setPreviewDownloading(true);
            const data = await api.post<{ blob: Blob; filename: string | null }>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}/preview`,
                { data: { pages, reviewOutcome }, timeoutMs: 15 * 60000 },
            );

            const filename = data.filename || `report-preview-${rid}.pdf`;
            const url = URL.createObjectURL(data.blob);
            triggerFileDownload({ url, filename });
            URL.revokeObjectURL(url);
        } catch (e) {
            log.error(e);
        } finally {
            setPreviewDownloading(false);
        }
    };

    return {
        noteDialogOpen,
        selectedStatus,
        openNoteModal,
        closeNoteModal,
        submitReview,
        previewDownloading,
        downloadPreview,
    };
};

export { useFooterActions };
